import { useState } from 'react';

import { api, type Audience, type ImportResult } from '../lib/api';
import { Alert, Badge, Card, Field, PageHead, formatTime, useLoader } from '../components/ui';

/**
 * Section 4.9 / 5.1: contact lists.
 *
 * The import flow is where the care goes, because a bad import is how a list
 * gets ruined — and the two things that matter are that re-importing does not
 * duplicate anyone, and that every rejected row is shown with its line number.
 * Both are surfaced explicitly rather than hidden behind a success count.
 */
export function Audiences() {
  const { data, error, loading, reload } = useLoader(() => api.audiences.list());
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);

  async function create(event: React.FormEvent) {
    event.preventDefault();
    setFormError(null);
    try {
      await api.audiences.create(name);
      setName('');
      setCreating(false);
      reload();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Could not create audience');
    }
  }

  return (
    <>
      <PageHead
        title="Audiences"
        subtitle="Contact lists for broadcasts. Unsubscribes are recorded here and on the suppression list, so a campaign and a transactional send agree about who opted out."
        actions={
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setCreating(!creating)}
          >
            {creating ? 'Cancel' : 'New audience'}
          </button>
        }
      />

      {error && <Alert kind="error">{error}</Alert>}

      {creating && (
        <Card title="New audience">
          {formError && <Alert kind="error">{formError}</Alert>}
          <form onSubmit={create} className="row" style={{ alignItems: 'flex-end' }}>
            <Field
              id="audience-name"
              label="Name"
              style={{ marginBottom: 0, flex: '1 1 260px' }}
            >
              {(props) => (
                <input
                  {...props}
                  required
                  placeholder="Newsletter subscribers"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                />
              )}
            </Field>
            <button className="btn btn-primary">Create</button>
          </form>
        </Card>
      )}

      {(data ?? []).map((audience) => (
        <AudienceCard
          key={audience.id}
          audience={audience}
          open={openId === audience.id}
          onToggle={() => setOpenId(openId === audience.id ? null : audience.id)}
          onChanged={reload}
        />
      ))}

      {(data ?? []).length === 0 && (
        <Card>
          <div className="empty">
            {loading ? 'Loading…' : 'No audiences yet. Create one, then import contacts.'}
          </div>
        </Card>
      )}
    </>
  );
}

function AudienceCard({
  audience,
  open,
  onToggle,
  onChanged,
}: {
  audience: Audience;
  open: boolean;
  onToggle: () => void;
  onChanged: () => void;
}) {
  const [search, setSearch] = useState('');
  const [importing, setImporting] = useState(false);
  const [csv, setCsv] = useState('');
  const [result, setResult] = useState<ImportResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const contacts = useLoader(
    () =>
      open
        ? api.audiences.contacts(audience.id, { limit: 200, search: search || undefined })
        : Promise.resolve([]),
    [open, search],
  );

  const unsubscribed = audience.contact_count - audience.subscribed_count;
  // Mailable but never confirmed: imported with an assertion, or predating double
  // opt-in. Not an error, and not something to hide either — this is the number
  // somebody would be asked about, so it goes next to the reassuring one.
  const unconfirmed = audience.subscribed_count - audience.confirmed_count;

  async function runImport(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      setResult(await api.audiences.importCsv(audience.id, csv));
      setCsv('');
      contacts.reload();
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Import failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card
      title={audience.name}
      actions={
        <div className="row">
          <Badge kind="muted">{audience.contact_count} contacts</Badge>
          <Badge kind="ok">{audience.subscribed_count} subscribed</Badge>
          {unconfirmed > 0 && (
            <Badge kind="warn">{unconfirmed} unconfirmed</Badge>
          )}
          {unsubscribed > 0 && <Badge kind="warn">{unsubscribed} unsubscribed</Badge>}
          <button
            type="button"
            className="btn btn-sm"
            onClick={() => {
              setImporting(!importing);
              setResult(null);
            }}
          >
            Import CSV
          </button>
          <button type="button" className="btn btn-sm" onClick={onToggle}>
            {open ? 'Hide contacts' : 'Contacts'}
          </button>
        </div>
      }
    >
      {unconfirmed > 0 && (
        // Visible rather than a tooltip: the number is meaningless without it,
        // and a title attribute is unreachable by keyboard. Stated plainly
        // because this is the sentence somebody may have to repeat to a
        // regulator.
        <p className="hint">
          {unconfirmed} of these are mailable without a confirmation click —
          imported with consent declared, or added before double opt-in. New
          contacts are asked to confirm and are not mailable until they do.
        </p>
      )}

      {importing && (
        <div>
          {/* Layout only: this wrapper holds an alert, a form and a result block,
              so it is not a .field. The field is the textarea inside it. */}
          {error && <Alert kind="error">{error}</Alert>}
          <form onSubmit={runImport}>
            <Field
              id={`csv-${audience.id}`}
              label="Paste CSV"
              hint={'The delimiter is detected, and column names are folded — “Email Address”, “First Name” and “Surname” all work. Re-importing updates contacts rather than duplicating them, and never re-subscribes anyone who opted out.'}
            >
              {(props) => (
                <textarea
                  {...props}
                  value={csv}
                  placeholder={'email,first_name,last_name\nada@example.com,Ada,Lovelace'}
                  onChange={(event) => setCsv(event.target.value)}
                />
              )}
            </Field>
            <div className="row" style={{ marginTop: 10 }}>
              <button className="btn btn-primary" disabled={busy || !csv.trim()}>
                {busy ? 'Importing…' : 'Import'}
              </button>
              <button type="button" className="btn" onClick={() => setImporting(false)}>
                Close
              </button>
            </div>
          </form>

          {result && (
            <div style={{ marginTop: 14 }}>
              <Alert kind={result.rejected > 0 ? 'warn' : 'ok'}>
                {result.created} added, {result.updated} updated
                {result.rejected > 0 ? `, ${result.rejected} rejected` : ''}.
              </Alert>
              {result.errors.length > 0 && (
                <div className="table-scroll">
                  <table>
                    <thead>
                      <tr>
                        <th>Line</th>
                        <th>Value</th>
                        <th>Why</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.errors.map((row, index) => (
                        <tr key={`${row.line}-${index}`}>
                          <td className="mono">{row.line}</td>
                          <td className="mono truncate">{row.value || '—'}</td>
                          <td>{row.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {open && (
        <div>
          <div className="row" style={{ marginBottom: 10 }}>
            <input
              aria-label="Search contacts by email"
              type="text"
              placeholder="Search by email…"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              style={{ maxWidth: 260 }}
            />
          </div>

          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Name</th>
                  <th>Status</th>
                  <th>Added</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {(contacts.data ?? []).map((contact) => (
                  <tr key={contact.id}>
                    <td className="mono">{contact.email}</td>
                    <td>
                      {[contact.first_name, contact.last_name].filter(Boolean).join(' ') ||
                        '—'}
                    </td>
                    <td>
                      {contact.subscribed ? (
                        <Badge kind="ok">
                          {/* Which of the two is showing matters: one is
                              evidence, the other is somebody's word for it. */}
                          {contact.consent_source === 'confirmed'
                            ? 'confirmed'
                            : 'subscribed'}
                        </Badge>
                      ) : contact.consent_source === 'pending' ? (
                        <Badge kind="muted">awaiting confirmation</Badge>
                      ) : (
                        <Badge kind="warn">unsubscribed</Badge>
                      )}
                    </td>
                    <td className="mono" style={{ whiteSpace: 'nowrap' }}>
                      {formatTime(contact.created_at)}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {contact.subscribed ? (
                        <button
                          type="button"
                          className="btn btn-sm"
                          onClick={async () => {
                            await api.contacts.unsubscribe(contact.id);
                            contacts.reload();
                            onChanged();
                          }}
                        >
                          Unsubscribe
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="btn btn-sm"
                          onClick={async () => {
                            await api.contacts.resubscribe(contact.id);
                            contacts.reload();
                            onChanged();
                          }}
                        >
                          Resubscribe
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {(contacts.data ?? []).length === 0 && (
            <div className="empty">
              {contacts.loading ? 'Loading…' : 'No contacts match.'}
            </div>
          )}
          <p className="hint" style={{ marginTop: 10 }}>
            Unsubscribing also adds the address to your suppression list, so it stops
            receiving transactional mail too. Resubscribing does not clear that — remove
            it from Suppressions if you mean to.
          </p>
        </div>
      )}
    </Card>
  );
}
